import { Core, Decorative } from "@faire/design-tokens";
import { localizePath } from "@faire/help-center-utils/lib/localization";
import { pageMaxWidthCss } from "@faire/help-center/components/Mixin";
import { Header } from "@faire/help-center/components/PageWrapper/Header/Header";
import { SEOOptimizedHeading } from "@faire/help-center/components/SEOOptimizedHeading";
import {
  BRANDS_BANNER_DATA,
  RETAILERS_BANNER_DATA,
} from "@faire/help-center/consts";
import { optimizeImageUrl } from "@faire/web--source/common/images/optimizeImages";
import { useStrictLocalization } from "@faire/web--source/common/localization";
import { Button } from "@faire/web--source/slate/Button";
import { Spacer, getSpacing } from "@faire/web--source/slate/spacing";
import { responsiveMediaQueries } from "@faire/web--source/slate/Theme/responsiveMediaQueries";
import { Typography } from "@faire/web--source/slate/Typography";
import { useViewport } from "@faire/web--source/ui/hooks/useViewport";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import { trackHelpCenterMakerCardClick } from "@faire/web-api--source/events/helpCenter/click/makerCard";
import { trackHelpCenterRetailerCardClick } from "@faire/web-api--source/events/helpCenter/click/retailerCard";
import brands from "@faire/web-api--source/routes/support/brands";
import retailers from "@faire/web-api--source/routes/support/retailers";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";

interface IHomeHeaderProps {
  loadTimingRef?: React.RefCallback<HTMLElement>;
}

export const HomeHeader: React.FC<IHomeHeaderProps> = ({ loadTimingRef }) => {
  const { strictLocalize } = useStrictLocalization();
  const { isMobile } = useViewport();
  const history = useHistory();

  return (
    <>
      <Header showSearch={false} />
      <Background ref={loadTimingRef}>
        <Container>
          <Spacer height="5x" />
          <SEOOptimizedHeading align="center">
            <StrictLocalMsg defaultMessage="Faire Help Center" />
          </SEOOptimizedHeading>
          <Spacer height="2x" />
          <Typography
            variant="subheadingSansRegular"
            align="center"
            color={Core.text.subdued}
          >
            <StrictLocalMsg defaultMessage="What can we help you with today?" />
          </Typography>
          <Spacer height="5x" />
          <CardWrapper>
            <Card
              data-test-id="help-with-buying-btn"
              onClick={() => {
                trackHelpCenterRetailerCardClick();
                history.push(localizePath(retailers()));
              }}
            >
              <Banner
                alt={RETAILERS_BANNER_DATA(strictLocalize).title}
                src={optimizeImageUrl(
                  {
                    url: RETAILERS_BANNER_DATA(strictLocalize).imageURL,
                  },
                  false,
                  {
                    resizeMethod: "fit",
                  }
                )}
              />
              <CardContent>
                <Typography variant="pageHeaderSerifRegular" align="center">
                  <StrictLocalMsg
                    defaultMessage="For retailers"
                    description={{ text: "As in 'this area is for retailers'" }}
                  />
                </Typography>
                <Spacer height="1x" />
                <Typography align="center" color={Core.text.subdued}>
                  {RETAILERS_BANNER_DATA(strictLocalize).copy}
                </Typography>
                <Spacer height="2x" />
                <Button variant="tertiary">
                  <StrictLocalMsg
                    defaultMessage="Help with Buying"
                    description={{
                      text: "A link to navigate to the retailer area on help center",
                    }}
                  />
                </Button>
              </CardContent>
            </Card>
            {isMobile ? <Spacer height="3x" /> : <Spacer width="7x" />}
            <Card
              onClick={() => {
                trackHelpCenterMakerCardClick();
                history.push(localizePath(brands()));
              }}
              data-test-id="help-with-selling-btn"
            >
              <Banner
                alt={BRANDS_BANNER_DATA(strictLocalize).title}
                src={optimizeImageUrl(
                  { url: BRANDS_BANNER_DATA(strictLocalize).imageURL },
                  false,
                  {
                    resizeMethod: "fit",
                  }
                )}
              />
              <CardContent>
                <Typography variant="pageHeaderSerifRegular" align="center">
                  <StrictLocalMsg
                    defaultMessage="For brands"
                    description={{ text: "As in 'this area is for brands'" }}
                  />
                </Typography>
                <Spacer height="1x" />
                <Typography align="center" color={Core.text.subdued}>
                  {BRANDS_BANNER_DATA(strictLocalize).copy}
                </Typography>
                <Spacer height="2x" />
                <Button variant="tertiary">
                  <StrictLocalMsg
                    defaultMessage="Help with Selling"
                    description={{
                      text: "A link to navigate to the brand area on help center",
                    }}
                  />
                </Button>
              </CardContent>
            </Card>
          </CardWrapper>
        </Container>
      </Background>
    </>
  );
};

const Background = styled.div`
  background-color: ${Decorative.tint.neutral};
`;

const Container = styled.div`
  ${pageMaxWidthCss}
  padding: ${getSpacing(0, "3x", "3x")};
  border-bottom: 1px solid ${Core.border.muted};

  ${responsiveMediaQueries.tabletAndAbove} {
    padding: ${getSpacing(0, "5x", "5x")};
  }

  ${responsiveMediaQueries.desktopAndAbove} {
    padding: ${getSpacing(0, "7x", "7x")};
  }
`;

const CardWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  ${responsiveMediaQueries.tabletAndAbove} {
    flex-direction: row;
    justify-content: center;
  }
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  background-color: ${Core.surface.inverse};
  border: 1px solid ${Core.border.muted};
  cursor: pointer;

  ${responsiveMediaQueries.tabletAndAbove} {
    width: 360px;
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${getSpacing("2x", "3x", "4x")};
`;

const Banner = styled.img`
  width: 100%;
  height: auto;

  ${responsiveMediaQueries.tabletAndAbove} {
    height: 208px;
  }
`;
